import React, { useState, useEffect } from 'react';
import { LeaderboardCategory, LeaderboardData } from '../types/leaderboard';
import { getLeaderboard, setLeaderboardVisibility } from '../lib/gameEngine';
import './LeaderboardMenu.css';

interface LeaderboardMenuProps {
  onBack: () => void;
  isGuest?: boolean;
}

const MAIN_CATEGORIES: { id: string; label: string }[] = [
  { id: 'bank_balance', label: 'Bank Balance' },
  { id: 'rarest_coin', label: 'Rarest Coin' },
  { id: 'under_100k', label: '<100k Count' },
  { id: 'collection_percentage', label: 'Set %' },
  { id: 'hunted', label: 'Coins Hunted' }
];

const HUNTED_SUBTABS: { id: LeaderboardCategory; label: string }[] = [
  { id: 'hunted_total', label: 'Total' },
  { id: 'hunted_2euro', label: '2€' },
  { id: 'hunted_1euro', label: '1€' },
  { id: 'hunted_50cent', label: '50¢' },
  { id: 'hunted_20cent', label: '20¢' },
  { id: 'hunted_10cent', label: '10¢' },
  { id: 'hunted_5cent', label: '5¢' },
  { id: 'hunted_2cent', label: '2¢' },
  { id: 'hunted_1cent', label: '1¢' }
];

export const LeaderboardMenu: React.FC<LeaderboardMenuProps> = ({ onBack, isGuest = false }) => {
  const [activeCategory, setActiveCategory] = useState<LeaderboardCategory>('bank_balance');
  const [activeHuntedSubtab, setActiveHuntedSubtab] = useState<LeaderboardCategory>('hunted_total');
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [isUserHidden, setIsUserHidden] = useState<boolean>(false);
  const [isPrivacyToggling, setIsPrivacyToggling] = useState<boolean>(false);

  const isHuntedCategory =
    activeCategory.startsWith('hunted_') || activeCategory === 'throughput';

  useEffect(() => {
    let mounted = true;
    setExpandedRows({});
    const fetchLeaderboard = async () => {
      setLoading(true);
      try {
        const data = await getLeaderboard(activeCategory);
        if (mounted && data) {
          setLeaderboardData(data);
          if (data.isCurrentUserHidden !== undefined) {
            setIsUserHidden(data.isCurrentUserHidden);
          }
        }
      } catch (err) {
        console.error('Failed to load leaderboard:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchLeaderboard();
    return () => {
      mounted = false;
    };
  }, [activeCategory]);

  const handleSelectMainCategory = (catId: string) => {
    if (catId === 'hunted') {
      setActiveCategory(activeHuntedSubtab);
    } else {
      setActiveCategory(catId as LeaderboardCategory);
    }
  };

  const handleSelectHuntedSubtab = (subtabId: LeaderboardCategory) => {
    setActiveHuntedSubtab(subtabId);
    setActiveCategory(subtabId);
  };

  const handleTogglePrivacy = async () => {
    if (isGuest || isPrivacyToggling) return;
    const newHidden = !isUserHidden;
    setIsUserHidden(newHidden);
    setIsPrivacyToggling(true);
    try {
      const res = await setLeaderboardVisibility(newHidden);
      if (res.success) {
        const refreshed = await getLeaderboard(activeCategory);
        if (refreshed) {
          setLeaderboardData(refreshed);
          if (refreshed.isCurrentUserHidden !== undefined) {
            setIsUserHidden(refreshed.isCurrentUserHidden);
          }
        }
      } else {
        setIsUserHidden(!newHidden);
      }
    } catch (err) {
      console.error('Failed to toggle leaderboard visibility:', err);
      setIsUserHidden(!newHidden);
    } finally {
      setIsPrivacyToggling(false);
    }
  };

  const toggleRow = (key: string) => {
    setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const currentData = leaderboardData;
  const isUserInTop10 = currentData?.top10?.some(entry => entry.isCurrentUser);
  const isRarestCoinTab = activeCategory === 'rarest_coin';

  const getRankClass = (rank: number) => {
    if (rank === 1) return 'rank-top-1';
    if (rank === 2) return 'rank-top-2';
    if (rank === 3) return 'rank-top-3';
    return '';
  };

  return (
    <div className="leaderboard-container">
      {/* Navigation */}
      <div className="leaderboard-nav">
        <button className="back-btn" onClick={onBack}>
          ← Back
        </button>
      </div>

      {/* Header */}
      <div className="leaderboard-header">
        <h1 className="leaderboard-title">Leaderboard</h1>
      </div>

      {/* Main Category Tabs */}
      <div className="leaderboard-tabs-nav">
        {MAIN_CATEGORIES.map(cat => {
          const isActive =
            cat.id === 'hunted' ? isHuntedCategory : activeCategory === cat.id;
          return (
            <button
              key={cat.id}
              className={`leaderboard-tab-btn ${isActive ? 'active' : ''}`}
              onClick={() => handleSelectMainCategory(cat.id)}
            >
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Denomination Subtabs for Rolls Hunted */}
      {isHuntedCategory && (
        <div className="leaderboard-subtabs-nav">
          {HUNTED_SUBTABS.map(sub => (
            <button
              key={sub.id}
              className={`leaderboard-subtab-btn ${activeCategory === sub.id ? 'active' : ''}`}
              onClick={() => handleSelectHuntedSubtab(sub.id)}
            >
              {sub.label}
            </button>
          ))}
        </div>
      )}

      {/* Rankings Table */}
      <div className="leaderboard-table-card">
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th className="col-rank">Rank</th>
              <th className="col-player">Player</th>
              <th className="col-value">
                {isRarestCoinTab ? (
                  <>
                    <span className="desktop-col-label">{currentData?.columnLabel || 'Lowest Mintage Coin'}</span>
                    <span className="mobile-col-label">Lowest Mintage</span>
                  </>
                ) : (
                  currentData?.columnLabel || 'Value'
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="leaderboard-status-cell">
                  Loading rankings...
                </td>
              </tr>
            ) : !currentData || currentData.top10.length === 0 ? (
              <tr>
                <td colSpan={3} className="leaderboard-status-cell">
                  No records found yet. Hunt rolls to appear here!
                </td>
              </tr>
            ) : (
              <>
                {currentData.top10.map((entry) => {
                  const rowKey = `top10-${entry.rank}-${entry.username}`;
                  const isExpanded = Boolean(expandedRows[rowKey]);
                  const isEntryHidden = Boolean(entry.isCurrentUser && (entry.isHidden ?? isUserHidden));
                  const displayValue = isRarestCoinTab
                    ? entry.valueDisplay.replace(/\s*mintage\s*/gi, '')
                    : entry.valueDisplay;

                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        className={`${entry.isCurrentUser ? `user-row ${isEntryHidden ? 'user-row-hidden' : ''}` : ''} ${isRarestCoinTab ? 'clickable-row' : ''} ${isExpanded ? 'row-expanded' : ''}`}
                        onClick={() => isRarestCoinTab && toggleRow(rowKey)}
                      >
                        <td className={`col-rank ${getRankClass(entry.rank)}`}>
                          #{entry.rank}
                        </td>
                        <td className={`col-player ${isEntryHidden ? 'player-name-hidden' : ''}`}>
                          <span className="player-name-text">{entry.username}</span>
                          {isEntryHidden && <span className="hidden-user-tag"> (hidden)</span>}
                          {entry.isCurrentUser && (
                            <span className={`user-badge ${isEntryHidden ? 'hidden-user-badge' : ''}`}>You</span>
                          )}
                        </td>
                        <td className="col-value">
                          <div className="stat-value-container">
                            {entry.secondaryInfo && (
                              <span
                                className={`secondary-stat ${isRarestCoinTab ? 'rarest-desktop-coin-name' : ''}`}
                                title={isRarestCoinTab ? entry.secondaryInfo : undefined}
                              >
                                {entry.secondaryInfo}
                              </span>
                            )}
                            <span className="primary-stat">{displayValue}</span>
                            {isRarestCoinTab && (
                              <span
                                className={`leaderboard-caret-indicator ${isExpanded ? 'expanded' : ''}`}
                                aria-hidden="true"
                              >
                                {isExpanded ? '▲' : '▼'}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>

                      {isRarestCoinTab && isExpanded && (
                        <tr className={`leaderboard-detail-row ${entry.isCurrentUser ? 'user-detail-row' : ''}`}>
                          <td colSpan={3} className="leaderboard-detail-cell">
                            <div className="rarest-coin-detail-box">
                              <span className="detail-label">Exact Coin:</span>
                              <span className="detail-value">{entry.secondaryInfo || 'Unknown coin'}</span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* If user is signed in and not in top 10, display divider and user row at bottom */}
                {!isUserInTop10 && currentData.currentUserRank && (() => {
                  const userRowKey = `user-${currentData.currentUserRank.rank}-${currentData.currentUserRank.username}`;
                  const isUserExpanded = Boolean(expandedRows[userRowKey]);
                  const isUserRankHidden = Boolean(currentData.currentUserRank.isHidden ?? isUserHidden);
                  const userDisplayValue = isRarestCoinTab
                    ? currentData.currentUserRank.valueDisplay.replace(/\s*mintage\s*/gi, '')
                    : currentData.currentUserRank.valueDisplay;

                  return (
                    <>
                      <tr className="user-rank-divider">
                        <td colSpan={3} className="divider-cell">
                          •••
                        </td>
                      </tr>
                      <tr
                        className={`user-row ${isUserRankHidden ? 'user-row-hidden' : ''} ${isRarestCoinTab ? 'clickable-row' : ''} ${isUserExpanded ? 'row-expanded' : ''}`}
                        onClick={() => isRarestCoinTab && toggleRow(userRowKey)}
                      >
                        <td className="col-rank">
                          #{currentData.currentUserRank.rank}
                        </td>
                        <td className={`col-player ${isUserRankHidden ? 'player-name-hidden' : ''}`}>
                          <span className="player-name-text">{currentData.currentUserRank.username}</span>
                          {isUserRankHidden && <span className="hidden-user-tag"> (hidden)</span>}
                          <span className={`user-badge ${isUserRankHidden ? 'hidden-user-badge' : ''}`}>You</span>
                        </td>
                        <td className="col-value">
                          <div className="stat-value-container">
                            {currentData.currentUserRank.secondaryInfo && (
                              <span
                                className={`secondary-stat ${isRarestCoinTab ? 'rarest-desktop-coin-name' : ''}`}
                                title={isRarestCoinTab ? currentData.currentUserRank.secondaryInfo : undefined}
                              >
                                {currentData.currentUserRank.secondaryInfo}
                              </span>
                            )}
                            <span className="primary-stat">
                              {userDisplayValue}
                            </span>
                            {isRarestCoinTab && (
                              <span
                                className={`leaderboard-caret-indicator ${isUserExpanded ? 'expanded' : ''}`}
                                aria-hidden="true"
                              >
                                {isUserExpanded ? '▲' : '▼'}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>

                      {isRarestCoinTab && isUserExpanded && (
                        <tr className="leaderboard-detail-row user-detail-row">
                          <td colSpan={3} className="leaderboard-detail-cell">
                            <div className="rarest-coin-detail-box">
                              <span className="detail-label">Exact Coin:</span>
                              <span className="detail-value">
                                {currentData.currentUserRank.secondaryInfo || 'Unknown coin'}
                              </span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })()}

                {/* If guest is viewing, prompt them to sign in */}
                {isGuest && (
                  <>
                    <tr className="user-rank-divider">
                      <td colSpan={3} className="divider-cell">
                        •••
                      </td>
                    </tr>
                    <tr className="guest-prompt-row">
                      <td colSpan={3} className="guest-prompt-cell">
                        Sign in with Discord to save your finds and rank on the leaderboard!
                      </td>
                    </tr>
                  </>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Subtle Privacy Toggle at Bottom */}
      {!isGuest && (
        <div className="leaderboard-bottom-controls">
          <label className="leaderboard-subtle-toggle">
            <input
              type="checkbox"
              checked={isUserHidden}
              onChange={handleTogglePrivacy}
              disabled={isPrivacyToggling}
            />
            <span className="subtle-checkbox" />
            <span className="subtle-toggle-text">
              Hide from leaderboards
            </span>
          </label>
        </div>
      )}
    </div>
  );
};
